// src/services/flowService.js
//
// Motor de pasos del reclamo/cheque (PLAN_ESTRUCTURA_PASOS.md, Etapa 1).
// La definición del proceso vive en config/claimFlow.js; acá solo se
// valida y se registra el movimiento entre pasos.
//
// Datos en Firestore:
//   {claimRecords|checks}/{id}.flow            → { type, stepId, phase, actor, enteredAt, updatedBy, updatedByRole, lastAction }
//   {claimRecords|checks}/{id}/flowHistory/{h} → movimiento inmutable (auditoría)
//   {claimRecords|checks}/{id}/stepData/{step} → datos propios de cada paso (los llenan los módulos)
//
// `enteredAt` se guarda desde el primer día aunque aún no haya
// temporizadores: cuando lleguen, los plazos se calculan hacia atrás sin
// migrar nada.
//
// El `role` que reciben estas funciones es solo para dar un error claro en
// la UI; la autoridad real son las reglas de firestore.rules.
import { auth, db } from '../firebase/config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  setDoc,
  Timestamp
} from 'firebase/firestore';
import { FLOWS, FINAL_STEPS } from '../config/claimFlow';
import { getBlockers } from '../config/flowRules';
import { appendActivity } from './workspaceService';

const COLLECTION_BY_TYPE = { claim: 'claimRecords', check: 'checks' };

// ── Funciones puras (sin Firestore) ─────────────────────────────────────

export const getFlow = (type) => {
  const flow = FLOWS[type];
  if (!flow) throw new Error(`Unknown flow type: ${type}`);
  return flow;
};

export const getStep = (type, stepId) => getFlow(type).steps.find(step => step.id === stepId) || null;

// Pasos del cobro de CSR que el CSR Assistant no toca (los maneja el Super Admin).
export const CSR_ASSISTANT_BLOCKED_STEPS = ['csr_payment', 'check_archived'];

// 'admin' = equipo CSR (puede todo); 'adjuster' = PA o su asistente (solo pasos
// actor 'pa'); 'csr_assistant' = pasos 'pa' y 'csr', menos el cobro de CSR.
export const canActOnStep = (step, role) => {
  if (!step || step.kind === 'final') return false;
  if (role === 'admin') return true;
  if (role === 'csr_assistant') return ['pa', 'csr'].includes(step.actor) && !CSR_ASSISTANT_BLOCKED_STEPS.includes(step.id);
  return role === 'adjuster' && step.actor === 'pa';
};

// A qué paso lleva completar `currentStepId` (con `decisionId` si el paso
// tiene opciones). Lanza error si la transición no existe.
export const resolveNextStep = (type, currentStepId, decisionId) => {
  const step = getStep(type, currentStepId);
  if (!step) throw new Error(`Unknown step: ${currentStepId}`);
  if (step.kind === 'final') throw new Error('Final steps cannot be completed');
  if (step.options) {
    const option = step.options.find(o => o.id === decisionId);
    if (!option) throw new Error(`Step ${currentStepId} requires one of: ${step.options.map(o => o.id).join(', ')}`);
    return option.next;
  }
  if (!step.next) throw new Error(`Step ${currentStepId} has no next step`);
  return step.next;
};

const flowState = (type, stepId, byEmail, byRole, lastAction, now) => {
  const step = getStep(type, stepId);
  return {
    type,
    stepId,
    phase: step.phase,
    actor: step.actor,
    enteredAt: now,
    updatedBy: byEmail,
    updatedByRole: byRole,
    lastAction
  };
};

// Nombre del paso actual de un reclamo, o '' si todavía no tiene (anterior al flujo).
export const claimStepLabel = (claimRecord, language = 'es') => {
  const step = claimRecord?.flow?.stepId ? getStep('claim', claimRecord.flow.stepId) : null;
  return step ? step.label[language === 'es' ? 'es' : 'en'] : '';
};

// Estado inicial para un documento nuevo (lo usa createClaimRecord).
export const buildInitialFlow = (type, byEmail, byRole = 'admin') =>
  flowState(type, getFlow(type).start, byEmail, byRole, 'created', Timestamp.now());

// ── Firestore ───────────────────────────────────────────────────────────

const currentEmail = () => auth.currentUser?.email || '';

const recordRef = (type, recordId) => doc(db, COLLECTION_BY_TYPE[type], recordId);

// Escribe el paso nuevo y su entrada de historial en una sola transacción.
// `expectedStepId` evita pisar un movimiento hecho por otra persona
// mientras esta pantalla estaba abierta.
// `extraFields`: otros campos del registro que tienen que cambiar junto con el
// paso (misma transacción: o se guarda todo o nada).
const transition = async ({ type, recordId, expectedStepId, toStepId, decisionId, reason, role, roleKey, lastAction, context, extraFields }) => {
  const byEmail = currentEmail();
  const now = Timestamp.now();
  const ref = recordRef(type, recordId);
  // El CSR Assistant no puede leer `checks` (trae lo que gana CSR): valida contra
  // el espejo sin datos financieros y solo ESCRIBE el paso en el cheque.
  const readRef = type === 'check' && role === 'csr_assistant' ? doc(db, 'checkSummaries', recordId) : ref;
  const flow = await runTransaction(db, async tx => {
    const snap = await tx.get(readRef);
    if (!snap.exists()) throw new Error('Record not found');
    const fromStepId = snap.data().flow?.stepId || null;
    if (expectedStepId !== undefined && fromStepId !== expectedStepId) {
      throw new Error('STEP_CHANGED');
    }
    // Reglas del documento del cliente (config/flowRules.js), con los datos
    // recién leídos. Solo al completar: "Mover a…" es la salida manual.
    if (context) {
      const blockers = getBlockers(type, { id: recordId, ...snap.data() }, context, decisionId);
      if (blockers.length) {
        const error = new Error('BLOCKED');
        error.blockers = blockers;
        throw error;
      }
    }
    const nextFlow = flowState(type, toStepId, byEmail, role, lastAction, now);
    tx.update(ref, { ...(extraFields || {}), flow: nextFlow });
    tx.set(doc(collection(ref, 'flowHistory')), {
      fromStep: fromStepId,
      toStep: toStepId,
      decision: decisionId || null,
      reason: reason || '',
      action: lastAction,
      byEmail,
      byRole: role,
      // Rol real de la cuenta que firma (pa, pa_assistant, csr_assistant…): Activity
      // registra cada movimiento con la cuenta que lo hizo.
      byRoleKey: roleKey || null,
      at: now
    });
    // Queda en "Actividad" del equipo, y marca si ahora le toca decidir al PA.
    // Los cheques también: el evento va al reclamo del cheque.
    const record = snap.data();
    const claimRecordId = type === 'claim' ? recordId : record.claimRecordId;
    if (claimRecordId) {
      const toStep = getStep(type, toStepId);
      const who = type === 'claim' ? '' : `Cheque ${record.checkNumber ? `#${record.checkNumber}` : ''} · `;
      appendActivity(tx, {
        event: 'claim_step',
        claimRecordId,
        ...(type === 'check' ? { checkId: recordId } : {}),
        // Quién puede ver este movimiento en su Activity (firestore.rules → notifications).
        companyName: record.companyName || '',
        adjusterName: record.adjusterName || '',
        accessNames: record.accessNames || [],
        reference: `${record.clientName || ''} · Claim ${record.claimNumber || '—'}: ${who}${getStep(type, fromStepId)?.label.es || '—'} → ${toStep.label.es}${reason ? ` (${reason})` : ''}`,
        fromStep: fromStepId,
        toStep: toStepId,
        decision: decisionId || null,
        byRoleKey: roleKey || null,
        awaitingPa: toStep.actor === 'pa'
      });
    }
    return nextFlow;
  });
  // Espejo sin datos de CSR (utils/checkSummary.js): refleja el paso nuevo. No
  // es crítico: `checks` ya quedó guardado; si falla, se corrige con la sincronización.
  if (type === 'check') {
    await setDoc(doc(db, 'checkSummaries', recordId), { flow, updatedAt: Timestamp.now() }, { merge: true })
      .catch(error => console.warn('checkSummaries no se pudo actualizar (no crítico):', error));
  }
  return flow;
};

// Completar el paso actual (con decisión si corresponde).
// `context` ({ stepData, closing }) activa las validaciones de flowRules.js.
export const completeStep = async ({ type, recordId, currentStepId, decisionId, reason, role, roleKey, context }) => {
  const step = getStep(type, currentStepId);
  if (!canActOnStep(step, role)) throw new Error('NOT_ALLOWED');
  const toStepId = resolveNextStep(type, currentStepId, decisionId);
  return transition({
    type, recordId, expectedStepId: currentStepId, toStepId, decisionId, reason, role, roleKey, context,
    lastAction: step.actor === 'pa' && role === 'adjuster' ? 'pa_decision' : 'complete'
  });
};

// Mover a cualquier paso (retroceder, saltar, cerrar). Solo equipo CSR y
// siempre con motivo — el proceso real no es 100% lineal.
export const moveToStep = async ({ type, recordId, currentStepId, toStepId, reason, role, roleKey, extraFields }) => {
  if (role !== 'admin') throw new Error('NOT_ALLOWED');
  if (!getStep(type, toStepId)) throw new Error(`Unknown step: ${toStepId}`);
  if (!reason || !reason.trim()) throw new Error('REASON_REQUIRED');
  return transition({
    type, recordId, expectedStepId: currentStepId, toStepId, reason: reason.trim(), role, roleKey, extraFields,
    lastAction: FINAL_STEPS.includes(toStepId) ? 'close' : 'move'
  });
};

export const getFlowHistory = async (type, recordId) => {
  const q = query(collection(recordRef(type, recordId), 'flowHistory'), orderBy('at', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getStepData = async (type, recordId, stepId) => {
  const snap = await getDoc(doc(recordRef(type, recordId), 'stepData', stepId));
  return snap.exists() ? snap.data() : {};
};

export const saveStepData = async (type, recordId, stepId, data) => {
  await setDoc(doc(recordRef(type, recordId), 'stepData', stepId), {
    ...data,
    updatedAt: Timestamp.now(),
    updatedBy: currentEmail()
  }, { merge: true });
};
